import {
  ShortcutArgs,
  Template,
  TemplateKey,
  templateKeyToCollection,
  UpdateCallback,
  WithId,
} from '@hb/shared'
import { collection, doc, setDoc, updateDoc } from 'firebase/firestore'
import { ComponentProps, PropsWithChildren, useCallback, useMemo } from 'react'

import { addMetadata, useApp } from '@hb/react'
import { db } from '../../../backend/db/db'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { TemplateEditorProvider } from './editor'
import { TemplatePracticeDataProvider, useTemplatePractice } from './practice'

const useAssessmentTemplateArgs = () => {
  const { practice } = useTemplatePractice()
  return useMemo<ShortcutArgs>(
    () => ({
      practice,
      assessmentId: null,
    }),
    [practice],
  )
}

const TemplateEditorProviderBody = ({
  children,
  onSubmit,
  template,
  onUpdateField,
  templateType,
  isLoading,
  templatePracticeId,
}: PropsWithChildren<{
  onSubmit: ComponentProps<typeof TemplateEditorProvider>['onSubmit']
  onUpdateField: ComponentProps<typeof TemplateEditorProvider>['onUpdateField']
  template: WithId<Template> | null
  templateType: TemplateKey
  isLoading: boolean
  templatePracticeId: string | null
}>) => {
  const shortcutArgs = useAssessmentTemplateArgs()
  return (
    <TemplateEditorProvider
      template={template}
      onSubmit={onSubmit}
      templatePracticeId={templatePracticeId}
      onUpdateField={onUpdateField}
      isLoading={isLoading}
      type={templateType}
      shortcutArgs={shortcutArgs}>
      {children}
    </TemplateEditorProvider>
  )
}

export const DocumentTemplateProvider = ({
  children,
  templateType,
  templateId,
  templatePracticeId,
}: PropsWithChildren<{
  templateType: TemplateKey
  templateId: string
  templatePracticeId: string | null
}>) => {
  const { appName } = useApp()

  const templateCollectionPath = useMemo(
    () => templateKeyToCollection[templateType],
    [templateType],
  )
  const { data: template, loading: isLoading } = useDocument<Template>(
    templateCollectionPath,
    templateId,
  )

  const onSubmit = useCallback(
    async (updated: Omit<Template, 'rank'>): Promise<UpdateCallback> => {
      const templateCollectionRef = collection(db, templateCollectionPath)
      const submitted = addMetadata(updated, appName, false)
      try {
        await setDoc(doc(templateCollectionRef, templateId), submitted, {
          merge: true,
        })
        return { success: 'Updated template!' }
      } catch (err: any) {
        console.error(err)
        return { error: err?.message || 'Error saving template' }
      }
    },
    [appName, templateId, templateCollectionPath],
  )

  const onUpdateField = useCallback(
    async (field: string, value: any): Promise<UpdateCallback> => {
      const templateCollectionRef = collection(db, templateCollectionPath)
      try {
        await updateDoc(doc(templateCollectionRef, templateId), field, value)
        return { success: 'Updated template!' }
      } catch (err: any) {
        console.error(err)
        return { error: err?.message || 'Error saving template' }
      }
    },
    [templateCollectionPath, templateId],
  )

  return (
    <TemplatePracticeDataProvider templatePracticeId={templatePracticeId}>
      <TemplateEditorProviderBody
        templateType={templateType}
        isLoading={isLoading}
        onUpdateField={onUpdateField}
        onSubmit={onSubmit}
        templatePracticeId={templatePracticeId}
        template={template}>
        {children}
      </TemplateEditorProviderBody>
    </TemplatePracticeDataProvider>
  )
}
